import { useCallback, useMemo, useState } from "react";

import { Keyboard, ViewportFit } from "@eweadn-qiankun/shared-components";

import SettingBefore from "../../../../../keyboard-management/src/pages/advancedKey/SettingBefore";

import SettingAfter from "../../../../../keyboard-management/src/pages/advancedKey/SettingAfter";

import { buildAdvancedKeyCheckDataFromListItem } from "../../../../../keyboard-management/src/pages/advancedKey/buildAdvancedKeyCheckData";

import type {
  CommunityPreviewDeviceMeta,
  FullBoardConfigData,
} from "@eweadn-qiankun/shared-types";

import {
  buildPreviewKeyboardLayoutWithChangeKey,
  resolveCommunityPreviewKeyboardType,
} from "../utils/buildPreviewKeyboardLayout";

import {
  buildAdvancedKeyAfterListFromConfig,
  buildAdvancedKeyModesFromConfig,
} from "../utils/boardConfigPreviewUtils";

import {
  getAdvancedKeyPreviewCatalog,
  resolveAdvancedKeyKeyboardClassName,
} from "../utils/advancedKeyPreviewUtils";

import "../../../../../keyboard-management/src/pages/advancedKey/index.scss";

interface Props {
  meta: CommunityPreviewDeviceMeta;

  configData?: FullBoardConfigData;
  /** 0=Win，1=Mac */
  currentDeviceSystem?: number;
}

export default function KeyboardAdvancedKeySection({
  meta,

  configData,
  currentDeviceSystem = 0,
}: Props) {
  const [detailOpen, setDetailOpen] = useState(false);

  const [checkData, setCheckData] = useState<Record<string, unknown> | null>(
    null
  );

  const previewKeyboardType = useMemo(
    () => resolveCommunityPreviewKeyboardType(meta),

    [meta]
  );

  const layout = useMemo(
    () =>
      buildPreviewKeyboardLayoutWithChangeKey(
        meta,
        configData,
        currentDeviceSystem
      ),
    [meta, configData, currentDeviceSystem]
  );

  const advancedKeyModes = useMemo(
    () => buildAdvancedKeyModesFromConfig(layout, configData),

    [layout, configData]
  );

  const advancedKeyAfterList = useMemo(
    () => buildAdvancedKeyAfterListFromConfig(layout, configData),

    [layout, configData]
  );

  const advancedKeyAllData = useMemo(
    () =>
      getAdvancedKeyPreviewCatalog(meta.solutionProvider, {
        solutionRovider: meta.solutionProvider,
        keyboardType: meta.keyboardType,
        displayname: meta.displayname,
        productName: meta.deviceCatalog?.productName,
        identifier: meta.deviceCatalog?.identifier,
      }),

    [meta]
  );

  const keyboardClassName = useMemo(
    () => resolveAdvancedKeyKeyboardClassName(meta.solutionProvider),

    [meta.solutionProvider]
  );

  const handleViewItem = useCallback(
    (item: Record<string, unknown>) => {
      const nextCheckData = buildAdvancedKeyCheckDataFromListItem(
        item,

        layout,

        advancedKeyAllData as Record<string, unknown>[]
      );

      if (!nextCheckData) return;

      setCheckData(nextCheckData);

      setDetailOpen(true);
    },

    [advancedKeyAllData, layout]
  );

  const handleCloseDetail = useCallback(() => {
    setDetailOpen(false);

    setCheckData(null);
  }, []);

  return (
    <div className="community-kb-advanced-preview advancedKey">
      <div className={keyboardClassName}>
        <ViewportFit>
          <div className="advanced-key-keyboard-display">
            <Keyboard
              keyboardType={previewKeyboardType as never}
              layout={layout}
              styleMode="neutral"
              selectionMode="multiple"
              selectedKeys={[]}
              advancedKeyModes={advancedKeyModes}
              showLabels
              enablePhysicalKeyboard={false}
              disableViewportFit
            />
          </div>
        </ViewportFit>
      </div>

      <div className="advancedKey-main community-preview-interactive">
        {!detailOpen ? (
          <SettingBefore
            advancedKeyAllData={advancedKeyAllData}
            advancedKeyAfterList={advancedKeyAfterList}
            getCheckData={() => undefined}
            getUpdate={() => undefined}
            onDelete={() => undefined}
            showKeyTest={false}
            previewMode
            onViewItem={handleViewItem}
          />
        ) : (
          <SettingAfter
            readOnly
            checkData={checkData}
            getAfterChangeData={() => undefined}
            onClose={handleCloseDetail}
            onSubmit={() => undefined}
          />
        )}
      </div>
    </div>
  );
}
